/**
 * Coin detayi - mobilde tam ekran sayfa, genis ekranda modal.
 *
 * Grafik burada, ana ekranda DEGILDIR ve yalnizca kullanici isterse
 * yuklenir ("Grafiği Aç"); boylece detay ekrani hizli acilir.
 *
 * VERI UYDURULMAZ: backend'de olmayan alan gosterilmez, "veri yok" yazar.
 * Seviyeler (direnc/destek) SR Channel Mirror ciktisidir; hedef fiyat
 * TAHMIN DEGILDIR, olcumdur.
 */

import { type MouseEvent, useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import type {
  Candles,
  LiquidityInfo,
  QuickMoveStatus,
  SignalDetail,
} from "../api/types";
import {
  ago,
  compact,
  dateTime,
  pct,
  price,
  utcHourMinute,
} from "../lib/format";
import { currentPrice, sinceSignalPct } from "../lib/signal";
import { TradingChart } from "./TradingChart";
import {
  Banner,
  Card,
  Change,
  IconClose,
  IconStar,
  Pill,
  Row,
  Stars,
} from "./Ui";

/** Kullanicinin fiilen gezdigi tek TF cifti - 15m<->4h. */
const OTHER_TIMEFRAME: Record<string, string> = { "15m": "4h", "4h": "15m" };

/** Kayma (slippage) islem buyuklugune baglidir; maliyet bu buyukluk icin
 *  olculur. Tipik bir manuel pozisyon varsayilir. */
const LIQUIDITY_SIZE_USDT = 1000;

function indicatorTone(position: number): "up" | "down" | "neutral" {
  if (position === 1) return "up";
  if (position === -1) return "down";
  return "neutral";
}

/** Binance spot islem sayfasi - SADECE link, API anahtari/emir YOK (bkz.
 *  kullanicinin acik karari: otomatik islem degil, tek-tikla manuel gecis). */
function binanceTradeUrl(base: string, quote: string): string {
  return `https://www.binance.com/tr/trade/${base}_${quote}?type=spot`;
}

/** TradingView grafigi (kullanici 10.10). Masaustu uygulamasi tarayicidan acilamiyor
 *  (tradingview:// orada yalnizca giris icin; bkz. TradingView Desktop). */
function tradingViewUrl(base: string, quote: string): string {
  return `https://www.tradingview.com/chart/?symbol=BINANCE:${base}${quote}`;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS kendini Mac gibi tanitir; dokunmatik Mac yok.
  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

function isMobile(): boolean {
  return isIos() || isAndroid();
}

/** Bilgisayarda TradingView masaustu uygulamasi (kullanici 11.10). Tarayici uygulamayi
 *  kendisi acamiyor; Windows'a kurulan "sinyalx-tv://" koprusu (kullanicinin bilgisayarinda
 *  %LOCALAPPDATA%/Sinyalix/tv-open.ps1) grafik linkini Windows uzerinden acar ve link
 *  "Web siteleri icin uygulamalar" ile TradingView Desktop'a gider. Kopru yalnizca o
 *  bilgisayarda kurulu oldugu icin secim CIHAZ basinadir (localStorage). */
const TV_DESKTOP_KEY = "tv.desktopApp";

function readTvDesktop(): boolean {
  try {
    return window.localStorage.getItem(TV_DESKTOP_KEY) === "1";
  } catch {
    return false;
  }
}

function writeTvDesktop(on: boolean): void {
  try {
    window.localStorage.setItem(TV_DESKTOP_KEY, on ? "1" : "0");
  } catch {
    // depolama kapali - secim yalnizca bu oturumda
  }
}

/** Telefonda uygulamayi acan adresler. */
type MobileApp = { iosUrl: string | null; androidUrl: string };

function intentUrl(target: string, pkg: string, web: string): string {
  const u = new URL(target);
  const scheme = u.protocol.replace(":", "");
  return (
    `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${scheme};` +
    `package=${pkg};S.browser_fallback_url=${encodeURIComponent(web)};end`
  );
}

/** Binance uygulamasi spot islem ekrani. Adres Binance Android uygulamasinin icinden
 *  (10.10): bnc://app.binance.com/trade/trade?at=spot&symbol=btcusdt (kucuk harf, alt
 *  cizgisiz). bnc:// iOS'ta da uygulamayi acar (dev.binance.vision). */
const BINANCE_APP = (base: string, quote: string, web: string): MobileApp => {
  const app = `bnc://app.binance.com/trade/trade?at=spot&symbol=${(base + quote).toLowerCase()}`;
  return { iosUrl: app, androidUrl: intentUrl(app, "com.binance.dev", web) };
};

/** TradingView grafigi. tradingview:// yalnizca giris icin (uygulamanin icinden, 10.10).
 *  Android: /chart/?symbol= App Link'i uygulamayi o coinle acar. iPhone: TradingView'in
 *  apple-app-site-association'i /chart/?symbol=... linklerini BILEREK disliyor - iPhone'da
 *  uygulamayi belirli coinle acan link YOK; bu yuzden iPhone'da site grafigi acilir (iosUrl null). */
const TRADINGVIEW_APP = (web: string): MobileApp => ({
  iosUrl: null,
  androidUrl: intentUrl(web, "com.tradingview.tradingviewapp", web),
});

/**
 * Telefonda linki UYGULAMADA acar (kullanici 10.10: "uygulamayi acmiyor sitede aciyor").
 * Neden: Sinyalix ana ekrandan (PWA, display: standalone) acilinca iPhone dis linkleri kendi
 * ic tarayicisinda acar ve Universal Link calismaz. iOS: uygulamanin kendi adresi. Android: intent:// (uygulama yoksa Chrome siteyi acar).
 * Masaustunde link normal davranir.
 */
function openInApp(event: MouseEvent<HTMLAnchorElement>, app: MobileApp): void {
  if (isAndroid()) {
    event.preventDefault();
    window.location.href = app.androidUrl;
    return;
  }
  if (!isIos() || app.iosUrl === null) return;
  event.preventDefault();
  // Site yedegi YOK (kullanici 11.10: "Safari'ye yonlendiriyor"): iPhone once "uygulamada
  // acilsin mi?" diye soruyor; soru ekrandayken sure doluyor ve yedek siteyi aciyordu.
  window.location.href = app.iosUrl;
}

/**
 * Girişte desteğe uzaklığa göre ÖLÇÜLMÜŞ "büyük düşüş" (girişten sonra
 * herhangi bir anda >=%10 düşme) olasılığı - bkz. research/mae_risk_probe.py
 * (10.650 işlem, 4H, %95 güven aralıkları örtüşmüyor - istatistiksel olarak
 * anlamlı, monoton bir ilişki). Taban çizgisi (tüm işlemler) %3,3'tür.
 */
const BIG_DROP_BUCKETS: { maxPct: number | null; probabilityPct: number }[] = [
  { maxPct: 5, probabilityPct: 2.1 },
  { maxPct: 15, probabilityPct: 4.2 },
  { maxPct: 30, probabilityPct: 6.5 },
  { maxPct: null, probabilityPct: 17.1 },
];
const BIG_DROP_BASELINE_PCT = 3.3;
/** Bu esigin ALTINDAKI olasiliklar tabana yakin/altinda - uyari GEREKMEZ. */
const BIG_DROP_WARNING_THRESHOLD_PCT = 6.0;

function bigDropProbability(supportDistancePct: number | null): number {
  if (supportDistancePct == null) return BIG_DROP_BASELINE_PCT; // "destek yok" grubu
  for (const bucket of BIG_DROP_BUCKETS) {
    if (bucket.maxPct == null || supportDistancePct < bucket.maxPct)
      return bucket.probabilityPct;
  }
  return BIG_DROP_BASELINE_PCT;
}

/** Likidite kademesi - API'deki makine degeri (HIGH/MEDIUM/LOW/VERY_LOW)
 *  degismez, YALNIZCA ekranda gosterilen metin Turkcelestirilir. */
const LIQUIDITY_TR: Record<string, string> = {
  HIGH: "Yüksek",
  MEDIUM: "Orta",
  LOW: "Düşük",
  VERY_LOW: "Çok düşük",
};

function indicatorLabel(position: number, signal: string): string {
  if (signal === "BUY") return "Yükseliş (bu bar)";
  if (signal === "SELL") return "Düşüş (bu bar)";
  if (position === 1) return "Yükseliş tarafında";
  if (position === -1) return "Düşüş tarafında";
  return "Yön yok";
}

export function CoinSheet({
  symbol,
  timeframe,
  theme,
  onClose,
  onFavoriteChange,
  onError,
}: {
  symbol: string;
  timeframe: string;
  theme: "light" | "dark";
  onClose: () => void;
  onFavoriteChange: (favorites: string[]) => void;
  onError: (message: string) => void;
}) {
  const [detail, setDetail] = useState<SignalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [candles, setCandles] = useState<Candles | null>(null);
  const [chartOpen, setChartOpen] = useState(false);
  const [tvDesktop, setTvDesktop] = useState(readTvDesktop);
  const tvDesktopActive = tvDesktop && !isMobile();
  const [chartLoading, setChartLoading] = useState(false);
  const [otherTfSignal, setOtherTfSignal] = useState<{
    timeframe: string;
    side: string;
    label: string;
  } | null>(null);
  const [quickMoveStatus, setQuickMoveStatus] =
    useState<QuickMoveStatus | null>(null);
  const [liquidity, setLiquidity] = useState<LiquidityInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setChartOpen(false);
    setCandles(null);
    void (async () => {
      try {
        const payload = await api.signalDetail(symbol, timeframe);
        if (!cancelled) setDetail(payload);
      } catch (error) {
        if (!cancelled) {
          onError(
            error instanceof ApiError
              ? error.message
              : `${symbol} detayı alınamadı`,
          );
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe, onClose, onError]);

  useEffect(() => {
    // Emir defterinden GERCEK islem maliyeti. Tek bir REST cagrisi (agirlik
    // 5) ve sunucuda 10 sn onbellekli - coin acildiginda bir kez okunur.
    // YARDIMCI bilgidir: hata olursa sessizce yok sayilir, detayi bloklamaz.
    let cancelled = false;
    setLiquidity(null);
    void (async () => {
      try {
        const payload = await api.liquidity(
          symbol,
          LIQUIDITY_SIZE_USDT,
          timeframe,
        );
        if (!cancelled) setLiquidity(payload);
      } catch {
        /* olcum yoksa kart hic gosterilmez */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe]);

  useEffect(() => {
    // Cok-zaman-dilimli celiski uyarisi: ayni sembolde DIGER TF'de de
    // aktif bir onaylanmis sinyal varsa, bu SIKLIKLA ayni hareketin farkli
    // gorunumudur (bkz. SIGNUSDT ornegi bu oturumda) - ayri bir firsat
    // SAYILMAMALI. YARDIMCI bilgidir; basarisiz olursa SESSIZCE yok sayilir,
    // ana detayi bloklamaz.
    const other = OTHER_TIMEFRAME[timeframe];
    setOtherTfSignal(null);
    if (!other) return;
    let cancelled = false;
    void (async () => {
      try {
        const payload = await api.signalDetail(symbol, other);
        if (!cancelled && payload.confirmed_side && payload.confirmed_label) {
          setOtherTfSignal({
            timeframe: other,
            side: payload.confirmed_side,
            label: payload.confirmed_label,
          });
        }
      } catch {
        /* yardimci bilgi - hata sessizce yok sayilir */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe]);

  useEffect(() => {
    // Quick-Move anlik durumu - YARDIMCI bilgidir (bkz. otherTfSignal ile
    // ayni desen): ana sinyal detayini bloklamaz, basarisiz olursa
    // sessizce "available: false" gibi davranir (kart gizlenir).
    setQuickMoveStatus(null);
    let cancelled = false;
    void (async () => {
      try {
        const payload = await api.quickMoveStatus(symbol);
        if (!cancelled) setQuickMoveStatus(payload);
      } catch {
        /* yardimci bilgi - hata sessizce yok sayilir */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const openChart = useCallback(async () => {
    setChartOpen(true);
    if (candles) return;
    setChartLoading(true);
    try {
      setCandles(await api.candles(symbol, timeframe, 300));
    } catch (error) {
      onError(
        error instanceof ApiError ? error.message : "Grafik verisi alınamadı",
      );
      setChartOpen(false);
    } finally {
      setChartLoading(false);
    }
  }, [candles, symbol, timeframe, onError]);

  const toggleFavorite = useCallback(async () => {
    try {
      const payload = await api.toggleFavorite(symbol);
      onFavoriteChange(payload.favorites);
      setDetail((current) =>
        current ? { ...current, is_favorite: payload.is_favorite } : current,
      );
    } catch (error) {
      onError(
        error instanceof ApiError
          ? error.message
          : "Takip listesi güncellenemedi",
      );
    }
  }, [symbol, onFavoriteChange, onError]);

  const active = detail?.active ?? null;

  // Buyuk dusus RISKI - kullanici geri bildirimi (NILUSDT ornegi) uzerine
  // research/mae_risk_probe.py ile OLCULDU: giristen sonraki EN KOTU dusus
  // (final kar/zarar degil) desteğe uzaklıkla GUCLU, ANLAMLI, monoton bir
  // iliski gosterdi (10.650 islem, GA'lar ortusmuyor). Yalnizca AL
  // sinyalinde anlamlidir; destek HIC BULUNAMAZSA taban orana (%3,3)
  // esitlenir - o grup arastirmada RISKLI cikmadi.
  const supportDistancePct =
    active?.side === "BUY" &&
    detail?.nearest_support != null &&
    active.signal_price
      ? ((active.signal_price - detail.nearest_support) / active.signal_price) *
        100
      : null;
  const bigDropRiskPct =
    active?.side === "BUY" ? bigDropProbability(supportDistancePct) : null;
  const supportFarWarning =
    bigDropRiskPct != null && bigDropRiskPct >= BIG_DROP_WARNING_THRESHOLD_PCT;

  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`${symbol} detayı`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <header className="sheet-header">
          <div>
            <div className="sheet-title">{symbol}</div>
            <div className="sheet-sub">
              {detail
                ? `${detail.base_asset} / ${detail.quote_asset}`
                : "Yükleniyor…"}{" "}
              · {timeframe.toUpperCase()}
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={toggleFavorite}
            aria-label={
              detail?.is_favorite
                ? "Elimde yok olarak işaretle"
                : "Elimde var olarak işaretle"
            }
            title="Elimde var işaretlenirse bu coinin Düşüş sinyalleri tarama ekranında çıkış noktası olarak vurgulanır."
            data-active={detail?.is_favorite ? "true" : "false"}
          >
            <IconStar filled={detail?.is_favorite ?? false} />
          </button>
          <button
            type="button"
            className="icon-btn sheet-close"
            onClick={onClose}
            aria-label="Kapat"
          >
            <IconClose />
          </button>
        </header>

        <div className="sheet-body">
          {loading || !detail ? (
            <div className="signal-list">
              <div className="skeleton" />
              <div className="skeleton" />
            </div>
          ) : (
            <>
              <div className="price-head">
                <span className="price-now">
                  {price(
                    detail.active ? currentPrice(detail.active) : detail.price,
                  )}
                </span>
                <span>
                  <Change value={detail.active?.change_24h_pct ?? null} />
                  <span className="note"> 24 saat</span>
                </span>
                {detail.source === "MOCK" ? (
                  <Pill tone="down">MOCK VERİ</Pill>
                ) : null}
              </div>

              {/* Islem butonlari fiyatin hemen altinda (kullanici 11.10); grafik acilinca
                  butonlarin altinda gorunur - en alta kaydirmak gerekmez. */}
              <div className="coin-actions">
                <div className="btn-row">
                  {chartOpen ? null : (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void openChart()}
                    >
                      Grafiği Aç
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void toggleFavorite()}
                  >
                    {detail.is_favorite ? "Elimde Yok" : "Elimde Var"}
                  </button>
                  <a
                    className="btn"
                    href={binanceTradeUrl(
                      detail.base_asset,
                      detail.quote_asset,
                    )}
                    onClick={(event) =>
                      openInApp(
                        event,
                        BINANCE_APP(
                          detail.base_asset,
                          detail.quote_asset,
                          binanceTradeUrl(
                            detail.base_asset,
                            detail.quote_asset,
                          ),
                        ),
                      )
                    }
                  >
                    Binance'de Aç
                  </a>
                  <a
                    className="btn"
                    href={
                      tvDesktopActive
                        ? `sinyalx-tv://${detail.base_asset}${detail.quote_asset}`
                        : tradingViewUrl(detail.base_asset, detail.quote_asset)
                    }
                    // Masaustunde (site) Sinyalix acik kalsin diye yeni sekme.
                    target={
                      isMobile() || tvDesktopActive ? undefined : "_blank"
                    }
                    rel="noopener noreferrer"
                    onClick={(event) =>
                      openInApp(
                        event,
                        TRADINGVIEW_APP(
                          tradingViewUrl(detail.base_asset, detail.quote_asset),
                        ),
                      )
                    }
                  >
                    TradingView'de Aç
                  </a>
                </div>
                {isMobile() ? null : (
                  <label className="note tv-desktop-toggle">
                    <input
                      type="checkbox"
                      checked={tvDesktop}
                      onChange={(event) => {
                        setTvDesktop(event.target.checked);
                        writeTvDesktop(event.target.checked);
                      }}
                    />{" "}
                    TradingView'i bu bilgisayardaki uygulamada aç
                  </label>
                )}
                {detail.is_favorite ? (
                  <p className="note" style={{ marginTop: 8 }}>
                    Elimde var işaretli — bu coin için gelen Düşüş sinyalleri
                    tarama ekranında &quot;Çıkış Sinyali&quot; olarak
                    vurgulanır.
                  </p>
                ) : null}
              </div>

              {chartOpen ? (
                <Card title="Grafik">
                  <div className="chart-shell">
                    {chartLoading ? (
                      <div className="skeleton" style={{ height: 260 }} />
                    ) : (
                      <TradingChart data={candles} theme={theme} height={260} />
                    )}
                  </div>
                  <p className="note" style={{ marginTop: 8 }}>
                    Çizgiler backend'in Indicator Mirror çıktısıdır; arayüz
                    kendi indikatörünü hesaplamaz.
                  </p>
                </Card>
              ) : null}

              {supportFarWarning ? (
                <Banner tone="warn">
                  En yakın destek, sinyal fiyatından{" "}
                  <strong>%{supportDistancePct?.toFixed(0)}</strong> uzakta.
                  Ölçülen veriye göre (10.650 geçmiş işlem) bu grupta girişten
                  sonra{" "}
                  <strong>
                    %10+ düşüş yaşama olasılığı %{bigDropRiskPct?.toFixed(1)}
                  </strong>{" "}
                  — taban ortalamanın (%{BIG_DROP_BASELINE_PCT.toFixed(1)})
                  üzerinde.
                </Banner>
              ) : null}

              {otherTfSignal ? (
                <Banner>
                  Bu sembolde{" "}
                  <strong>{otherTfSignal.timeframe.toUpperCase()}</strong> zaman
                  diliminde de aktif bir <strong>{otherTfSignal.label}</strong>{" "}
                  sinyali var — genellikle aynı hareketin farklı görünümüdür,
                  ayrı bir fırsat olarak SAYMAYIN.
                </Banner>
              ) : null}

              <div className="indicator-grid">
                <div className="indicator-box">
                  <div className="indicator-name">UT Bot</div>
                  <div
                    className={`indicator-value ${indicatorTone(detail.ut_position)}`}
                  >
                    {indicatorLabel(detail.ut_position, detail.ut_signal)}
                  </div>
                </div>
                <div className="indicator-box">
                  <div className="indicator-name">Onaylı yön</div>
                  <div
                    className={`indicator-value ${
                      detail.confirmed_side === "BUY"
                        ? "up"
                        : detail.confirmed_side === "SELL"
                          ? "down"
                          : "muted"
                    }`}
                  >
                    {detail.confirmed_label ?? "Onaylanmış sinyal yok"}
                  </div>
                </div>
              </div>

              <Card title="Durum">
                <div className="rows">
                  <Row label="Onaylı sinyal">
                    <span
                      className={
                        detail.confirmed_side === "BUY"
                          ? "up"
                          : detail.confirmed_side === "SELL"
                            ? "down"
                            : "muted"
                      }
                    >
                      {detail.confirmed_label ?? "Onaylanmış sinyal yok"}
                    </span>
                  </Row>
                  {quickMoveStatus ? (
                    quickMoveStatus.available && quickMoveStatus.reading ? (
                      <>
                        <Row label="Anlık momentum">
                          <span
                            className={
                              quickMoveStatus.reading.velocity >= 0
                                ? "up"
                                : "down"
                            }
                          >
                            {pct(quickMoveStatus.reading.velocity, 3)}/dk
                          </span>
                          {quickMoveStatus.reading.above_strong_threshold ? (
                            <Pill tone="warn" title="Skor 6,5+ — güçlü hareket">
                              Güçlü · {quickMoveStatus.reading.score.toFixed(1)}
                            </Pill>
                          ) : null}
                        </Row>
                        <Row label="Yorum">
                          <span className="note">
                            {quickMoveStatus.comment_tr}
                          </span>
                        </Row>
                        <Row label="Veri">
                          <span className="note">
                            {ago(quickMoveStatus.reading.close_time)} alındı
                          </span>
                        </Row>
                      </>
                    ) : (
                      <Row label="Anlık momentum">
                        <span className="muted">
                          Yeterli yakın zamanlı veri yok
                        </span>
                      </Row>
                    )
                  ) : (
                    <Row label="Anlık momentum">
                      <span className="muted">Yükleniyor…</span>
                    </Row>
                  )}
                </div>
              </Card>

              {liquidity?.available && liquidity.round_trip_cost_pct != null ? (
                <Card title="Gerçek işlem maliyeti">
                  <div className="rows">
                    <Row label="Alış-satış farkı (spread)">
                      {liquidity.spread_pct != null
                        ? `%${liquidity.spread_pct.toFixed(3)}`
                        : "—"}
                    </Row>
                    <Row
                      label={`Kayma (${compact(liquidity.quote_amount)} USDT için)`}
                    >
                      {liquidity.estimated_slippage_pct != null
                        ? `%${liquidity.estimated_slippage_pct.toFixed(3)}`
                        : "—"}
                    </Row>
                    <Row label="Toplam gidiş-dönüş maliyeti">
                      <strong>
                        %{liquidity.round_trip_cost_pct.toFixed(3)}
                      </strong>
                    </Row>
                    {liquidity.expected_move_pct != null &&
                    liquidity.net_edge_pct != null ? (
                      <>
                        <Row label="Hedefe kadar beklenen hareket">
                          %{liquidity.expected_move_pct.toFixed(3)}
                        </Row>
                        <Row label="Maliyet sonrası net kenar">
                          <span
                            className={
                              liquidity.net_edge_pct > 0 ? "up" : "down"
                            }
                          >
                            <strong>
                              {liquidity.net_edge_pct > 0 ? "+" : ""}%
                              {liquidity.net_edge_pct.toFixed(3)}
                            </strong>
                          </span>
                        </Row>
                      </>
                    ) : null}
                    {!liquidity.is_tradable ? (
                      <Row label="Uyarı">
                        <span className="down">{liquidity.detail_tr}</span>
                      </Row>
                    ) : null}
                  </div>
                  <p className="note">
                    Komisyon (%0,10 × 2), spread ve kayma dahil; kayma{" "}
                    {compact(liquidity.quote_amount)} USDT'lik bir işlem için o
                    anki emir defterinden hesaplandı. Daha büyük işlemde kayma
                    artar.
                    {liquidity.net_edge_pct != null &&
                    liquidity.net_edge_pct <= 0
                      ? " Net kenar sıfırın altında: bu sinyalde hedefe ulaşılsa bile maliyet kârı yiyor."
                      : ""}
                  </p>
                </Card>
              ) : null}

              <Card title="Sinyal bilgileri">
                {active ? (
                  <div className="rows">
                    <Row label="Durum">
                      <Pill tone={active.side === "BUY" ? "up" : "down"}>
                        {active.signal_label || active.side_label} yürürlükte
                      </Pill>
                    </Row>
                    <Row label="Sinyal fiyatı">
                      {price(active.signal_price)}
                    </Row>
                    <Row label="Sinyalden beri">
                      <Change value={sinceSignalPct(active)} />
                    </Row>
                    <Row label="Sinyal zamanı">
                      {dateTime(active.signal_close_time)}
                      <span className="note">
                        {" "}
                        · {utcHourMinute(active.signal_close_time)} UTC
                      </span>
                    </Row>
                    <Row label="Sinyal yaşı">{active.age_bars} bar</Row>
                    <Row label="UT Bot olayı (kesişme)">
                      {active.event_close_time != null ? (
                        dateTime(active.event_close_time)
                      ) : (
                        <span className="muted">Veri yok</span>
                      )}
                    </Row>
                    <Row label="Onay mumu">
                      {timeframe.toUpperCase()} ·{" "}
                      {dateTime(active.signal_close_time)} kapanışı
                    </Row>
                    <Row label="Likidite (24s hacim)">
                      {active.liquidity_tier
                        ? (LIQUIDITY_TR[active.liquidity_tier] ??
                          active.liquidity_tier)
                        : "Veri yok"}
                    </Row>
                    <Row label="İlk direnç (hedef)">
                      {detail.nearest_resistance != null ? (
                        price(detail.nearest_resistance)
                      ) : (
                        <span className="muted">Veri yok</span>
                      )}
                    </Row>
                    <Row label="İlk destek">
                      {detail.nearest_support != null ? (
                        price(detail.nearest_support)
                      ) : (
                        <span className="muted">Veri yok</span>
                      )}
                    </Row>
                    <Row label="ATR">
                      {detail.atr != null ? (
                        `${price(detail.atr)} (${pct(detail.atr_pct)})`
                      ) : (
                        <span className="muted">Veri yok</span>
                      )}
                    </Row>
                  </div>
                ) : (
                  <p className="note">
                    Bu zaman diliminde yürürlükte onaylanmış sinyal yok. Kural:{" "}
                    {detail.rule_tr}.
                  </p>
                )}
                <p className="note" style={{ marginTop: 10 }}>
                  {detail.rule_tr}.
                </p>
              </Card>


              <Card title="Sinyal kalitesi">
                {detail.quality ? (
                  <>
                    <div className="row" style={{ marginBottom: 10 }}>
                      <span className="row-label">Kalite</span>
                      <span className="row-value">
                        <Stars
                          score={detail.quality.score}
                          max={detail.quality.max_score}
                        />{" "}
                        {detail.quality.score}/{detail.quality.max_score}
                      </span>
                    </div>
                    {detail.quality.is_high_quality ? (
                      <div className="row" style={{ marginBottom: 10 }}>
                        <span className="row-label">
                          Özel filtre
                          <br />
                          <span className="muted">
                            4h + Yükseliş için doğrulandı: kazanma %68.2 (bkz.
                            hacim+trend+volatilite+ kırılım+ADX≥20+dirençten
                            uzak)
                          </span>
                        </span>
                        <span className="row-value">
                          <Pill tone="up">★ Yüksek Kalite</Pill>
                        </span>
                      </div>
                    ) : null}
                    {active?.full_confluence ? (
                      <div className="row" style={{ marginBottom: 10 }}>
                        <span className="row-label">
                          Gösterge uyumu
                          <br />
                          <span className="muted">
                            Supertrend, EMA Crossover, Donchian Breakout ve
                            MACD'nin dördü de aynı anda yukarı trend diyor
                            (araştırmayla doğrulandı: train %+0,61, test %+0,52)
                          </span>
                        </span>
                        <span className="row-value">
                          <Pill tone="info">Tam Uyum</Pill>
                        </span>
                      </div>
                    ) : null}
                    {detail.quality.adx != null ? (
                      <div className="row" style={{ marginBottom: 10 }}>
                        <span className="row-label">ADX(14) — trend gücü</span>
                        <span className="row-value">
                          {detail.quality.adx.toFixed(1)}
                        </span>
                      </div>
                    ) : null}
                    <div className="rows">
                      {detail.quality.factors.map((factor) => (
                        <div className="row" key={factor.key}>
                          <span className="row-label">
                            {factor.label}
                            <br />
                            <span className="muted">{factor.detail}</span>
                          </span>
                          <span className="row-value">
                            <Pill tone={factor.passed ? "up" : "neutral"}>
                              {factor.passed ? "Onaylı" : "Yok"}
                            </Pill>
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="note">
                    Kalite hesaplanamadı (yeterli veri yok).
                  </p>
                )}
                <p className="note" style={{ marginTop: 10 }}>
                  Kalite skoru sinyali <strong>engellemez</strong>; yalnızca
                  çevresel koşulları özetler. Hangi faktörün gerçekten işe
                  yaradığı Backtest Lab ile ölçülecek.
                </p>
              </Card>

              <Card title="Son sinyal performansı">
                {detail.pairs.length === 0 ? (
                  <p className="note">
                    Tamamlanmış Yükseliş → Düşüş döngüsü yok (son{" "}
                    {detail.bars_evaluated} bar).
                  </p>
                ) : (
                  <div className="rows">
                    {detail.pairs.slice(0, 5).map((pair) => (
                      <div className="row" key={pair.entry_close_time}>
                        <span className="row-label">
                          Yükseliş {price(pair.entry_price)} → Düşüş{" "}
                          {price(pair.exit_price)}
                          <br />
                          <span className="muted">
                            Yükseliş {dateTime(pair.entry_close_time)} → Düşüş{" "}
                            {dateTime(pair.exit_close_time)} · {pair.bars_held}{" "}
                            bar
                          </span>
                        </span>
                        <span className="row-value">
                          <Change value={pair.gross_change_pct} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="note" style={{ marginTop: 10 }}>
                  Bu yüzdeler <strong>brüt sinyal hareketidir</strong>;
                  komisyon, spread ve slippage dahil değildir. Gerçek işlem
                  getirisi daha düşüktür.
                </p>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
